import * as R from 'ramda'
import React, { useState } from 'react'
import { useIsMobile } from '../../hooks'
import { twMerge } from 'tailwind-merge'
import { SetState } from '../../vite-env'
import { Icon } from '../Icon/Icon'
import {
  faBars,
} from '@fortawesome/free-solid-svg-icons'

type Children = {
  children: React.ReactNode
}
type ClassName = {
  className?: HTMLElement['className']
}

type TitleProps = {
  variant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  title?: string
  children?: React.ReactNode
  className?: string
  titleClasses?: string
  sidebarState?: [boolean, SetState<boolean>]
}

export const Header = ({
  children,
  title,
  className = '',
  titleClasses = '',
  variant = 'h1',
  sidebarState,
}: TitleProps) => {
  const internalState = useState(true)
  const [visible, setIsVisible] = sidebarState || internalState
  const isMobile = useIsMobile()

  const notIfMobile = isMobile ? R.not : R.identity<boolean>

  return (
    <header
      className={twMerge(
        `flex items-center py-2 sm:py-4 border-b border-base-200`,
        className,
      )}>
      {sidebarState && (
        <button className='pl-4' onClick={() => setIsVisible(R.not)}>
          <Icon
            className={twMerge(
              'p-1.5',
              notIfMobile(visible) && 'bg-blue-500 rounded-full text-white',
            )}
            icon={faBars}
          />
        </button>
      )}
      {variant === 'h1' && title && (
        <h1
          className={twMerge(
            'font-semibold text-base/7 text-base-900  dark:text-white sm:text-2xl text-center pr-4',
            titleClasses,
          )}>
          {title}
        </h1>
      )}
      {variant === 'h2' && title && (
        <h2
          className={twMerge(
            'font-semibold text-base-700 text-lg text-center',
            titleClasses,
          )}>
          {title}
        </h2>
      )}

      {variant === 'h3' && title && <h3>{title}</h3>}

      {children}
    </header>
  )
}

export const Container = (props: HTMLDivElement) => (
  <div className='page-container'></div>
)

const Body = ({ children }: Children) => (
  <div className='flex flex-col grow overflow-y-auto'>{children}</div>
)

const BodyContent = ({ children, className }: Children & ClassName) => (
  <div className={`flex grow overflow-y-auto ${className}`}>{children}</div>
)

type BodySidebarProps = {
  hidden?: boolean
} & Children &
  ClassName
const Sidebar = ({ children, hidden, className }: BodySidebarProps) => (
  <aside
    className={`p-4 border-r overflow-y-scroll w-screen sm:max-w-[250px] sm:min-w-[250px] sm:w-full ${className}`}>
    {children}
  </aside>
)

const Section = ({ children }: Children) => (
  <div className='flex flex-col grow overflow-hidden relative z-50'>
    {children}
  </div>
)

const SectionContent = ({ children, className }: Children & ClassName) => (
  <div className={`h-full w-full relative z-50 overflow-y-auto ${className}`}>
    {children}
  </div>
)

Body.Content = BodyContent
Section.Content = SectionContent
Container.Sidebar = Sidebar
Container.Body = Body
